import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Loader2, AlertCircle } from 'lucide-react';
import { mediaUrl } from '../../api/posts';
import { claimAudio, releaseAudio } from '../../lib/audioBus';

// Phase F7 — playback speeds. Persisted locally (localStorage only, no
// backend prefs): a new Racconto starts at the last chosen speed.
const RATES = [1, 1.25, 1.5, 2];
const RATE_KEY = 'nymia_playback_rate';
const getSavedRate = () => {
  try {
    const r = parseFloat(localStorage.getItem(RATE_KEY));
    return RATES.includes(r) ? r : 1;
  } catch { return 1; }
};

const fmt = (s) => {
  if (!Number.isFinite(s) || s < 0) s = 0;
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

/**
 * RaccontoPlayer — Phase F7. The ONE Racconto audio player, reused in the
 * Vetrina (inline) and in /post/:id. Dark+gold, mobile-first, no metrics.
 *
 * - Lazy media: NO network request until the user presses Play/seeks
 *   (the <audio> element is created on demand). Locked posts never reach
 *   this component (media_url is empty server-side).
 * - Exclusive playback via lib/audioBus (also coordinates Audio Avatar).
 * - Duration: real metadata wins; falls back to the persisted
 *   duration_seconds (streaming WebM has no container duration).
 */
const RaccontoPlayer = ({ post, showMeta = false, className = '' }) => {
  const [status, setStatus] = useState('idle'); // idle | loading | playing | paused | error
  const [current, setCurrent] = useState(0);
  const [realDur, setRealDur] = useState(0);
  const [rate, setRate] = useState(getSavedRate);
  const audioRef = useRef(null);
  const handleRef = useRef(null);

  const persistedDur = Number(post.duration_seconds || 0);
  const duration = realDur > 0 ? realDur : persistedDur;

  useEffect(() => () => {
    const a = audioRef.current;
    if (a) {
      try { a.pause(); a.removeAttribute('src'); a.load(); } catch { /* noop */ }
      releaseAudio(handleRef.current);
    }
  }, []);

  const ensureAudio = () => {
    if (audioRef.current) return audioRef.current;
    const a = new Audio();
    a.preload = 'metadata';
    a.src = mediaUrl(post.media_url);
    a.playbackRate = rate;
    const syncDur = () => {
      if (Number.isFinite(a.duration) && a.duration > 0) setRealDur(a.duration);
    };
    a.onloadedmetadata = syncDur;
    a.ondurationchange = syncDur;
    a.ontimeupdate = () => setCurrent(a.currentTime || 0);
    a.onwaiting = () => setStatus((st) => (st === 'playing' ? 'loading' : st));
    a.onplaying = () => setStatus('playing');
    a.onpause = () => setStatus((st) => (st === 'error' || st === 'idle' ? st : 'paused'));
    a.onended = () => {
      try { a.currentTime = 0; } catch { /* noop */ }
      setCurrent(0);
      setStatus('idle');
      releaseAudio(handleRef.current);
    };
    a.onerror = () => {
      setStatus('error');
      releaseAudio(handleRef.current);
    };
    audioRef.current = a;
    handleRef.current = { pause: () => { try { a.pause(); } catch { /* noop */ } } };
    return a;
  };

  const toggle = (e) => {
    e?.stopPropagation();
    const a = ensureAudio();
    if (status === 'playing' || status === 'loading') {
      a.pause();
      return;
    }
    setStatus('loading');
    claimAudio(handleRef.current);
    a.playbackRate = rate;
    a.play().then(() => setStatus('playing')).catch(() => setStatus('error'));
  };

  const onSeek = (e) => {
    e.stopPropagation();
    const v = parseFloat(e.target.value);
    if (!Number.isFinite(v)) return;
    setCurrent(v);
    const a = ensureAudio();
    try { a.currentTime = v; } catch { /* metadata not ready yet */ }
  };

  const cycleRate = (e) => {
    e?.stopPropagation();
    const next = RATES[(RATES.indexOf(rate) + 1) % RATES.length];
    setRate(next);
    try { localStorage.setItem(RATE_KEY, String(next)); } catch { /* noop */ }
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  if (status === 'error') {
    return (
      <div
        className={`flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-400 ${className}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={`player-error-${post.id}`}
      >
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        <span>Non è stato possibile caricare il Racconto.</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            try { audioRef.current?.pause(); } catch { /* noop */ }
            audioRef.current = null;
            setCurrent(0);
            setStatus('idle');
          }}
          className="ml-auto shrink-0 font-semibold underline underline-offset-2"
          data-testid={`player-retry-${post.id}`}
        >
          Riprova
        </button>
      </div>
    );
  }

  const playing = status === 'playing';
  return (
    <div
      className={`w-full min-w-0 ${className}`}
      onClick={(e) => e.stopPropagation()}
      data-testid={`racconto-player-${post.id}`}
    >
      {showMeta && (
        <div className="mb-1.5 min-w-0">
          <p className="line-clamp-1 text-sm font-semibold">{post.title || 'Racconto audio'}</p>
          {post.author?.username && (
            <p className="text-[11px] text-muted-foreground">@{post.author.username}</p>
          )}
        </div>
      )}
      <div className="flex items-center gap-2.5">
        <button
          onClick={toggle}
          aria-label={playing || status === 'loading' ? 'Pausa' : 'Play'}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full gold-gradient text-black shadow-sm transition-transform active:scale-95"
          data-testid={`player-play-btn-${post.id}`}
        >
          {status === 'loading' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : playing ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="ml-0.5 h-4 w-4" />
          )}
        </button>
        <input
          type="range"
          min={0}
          max={duration > 0 ? duration : 0}
          step={0.1}
          value={Math.min(current, duration > 0 ? duration : 0)}
          onChange={onSeek}
          disabled={!(duration > 0)}
          aria-label="Barra di avanzamento del Racconto"
          className="h-2 min-w-0 flex-1 cursor-pointer accent-amber-500 disabled:opacity-40"
          data-testid={`player-seek-${post.id}`}
        />
        <span
          className="shrink-0 text-[11px] tabular-nums text-muted-foreground"
          data-testid={`player-time-${post.id}`}
        >
          {fmt(current)} / {fmt(duration)}
        </span>
        <button
          onClick={cycleRate}
          aria-label={`Velocità di riproduzione: ${rate}x. Tocca per cambiare.`}
          className="shrink-0 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] font-bold gold-text"
          data-testid={`player-speed-${post.id}`}
        >
          {rate}x
        </button>
      </div>
    </div>
  );
};

export default RaccontoPlayer;
